import { put } from "@common/api/apiShared";
import { ajaxQueue } from "@common/api/ajaxQueue";
import { useDotLegalMutation, useDotLegalQueryClient } from "@common/hooks/dotLegalReactQuery";
import { LegalEntityAuditGroupDraft, LegalEntityAuditGroupDraftOverviewModel } from "./LegalEntityAuditGroupOverview.types";
import { withoutContact } from "../../legalEntity/legalEntityAuditTab/auditAdditionalRecipients.utils";

export function getAuditGroupDraftOverviewQueryKey(auditGroupId: string) {
    return `/legalEntityAuditGroups/${auditGroupId}`;
}

// The draft overview page and the contact pane both change the selection. The pane outlives the page, so both
// read the selection from the query cache at the moment they change it rather than from a render's snapshot.
export function useAuditGroupDraftSelection(auditGroupId: string) {
    const queryClient = useDotLegalQueryClient();
    const queryKey = getAuditGroupDraftOverviewQueryKey(auditGroupId);

    const updateSelectedEntitiesMutation = useDotLegalMutation(updateSelectedEntitiesApi, {
        onSuccess: (response) => {
            queryClient.setQueryData(queryKey, response.value());
        },
    });

    async function updateSelectedLegalEntities(selectedEntities: Array<LegalEntityAuditGroupDraft>) {
        queryClient.setQueryData<LegalEntityAuditGroupDraftOverviewModel>(queryKey, (current) => ({ ...current!, selectedEntities: selectedEntities }));

        await ajaxQueue().addRequest(updateSelectedEntitiesMutation.mutateAsync, {
            selectedEntities: selectedEntities,
        });
    }

    async function selectContactForLegalEntity(legalEntityId: string, contactId: string) {
        const draftOverview = queryClient.getQueryData<LegalEntityAuditGroupDraftOverviewModel>(queryKey);

        // Gone when the user left the draft page long enough ago for the cache to drop it; the contact is still created
        if (!draftOverview) {
            return;
        }

        const selectedEntities = draftOverview.selectedEntities.map((entity) =>
            entity.legalEntityId === legalEntityId
                ? new LegalEntityAuditGroupDraft(entity.legalEntityId, contactId, withoutContact(entity.additionalRecipientIds, contactId), entity.systemIds)
                : entity
        );

        await updateSelectedLegalEntities(selectedEntities);
    }

    return { updateSelectedLegalEntities, selectContactForLegalEntity };

    async function updateSelectedEntitiesApi(data: { selectedEntities: Array<LegalEntityAuditGroupDraft> }) {
        return await put("/legalEntityAuditGroups/" + auditGroupId + "/drafts/", data.selectedEntities);
    }
}
